import {
  exportToExcelMultiSheet,
  importFromExcelMultiSheet,
  generateMultiSheetTemplate,
  ExcelColumn,
  MultiSheetImportResult,
} from "./excelUtils";
import {
  RoadType,
  CutFillSection,
  CarriagewayFootway,
  FootwayConfiguration,
  CappingLayer,
  CappingType,
  FootpathConstructionType,
  FootpathLayerConfig,
  FootpathsConstructionConfig,
  FootpathColumnKey,
} from "../types/tender.types";
import { MAIN_SERVICE_TRENCHING_MAPPING } from "./mainServiceTrenchingMapping";

export interface RoadsExcelData {
  roadTypes: RoadType[];
  cutFillSections: CutFillSection[];
  carriagewayFootways: CarriagewayFootway[];
  footwayConfigurations: FootwayConfiguration[];
  cappingLayer: CappingLayer;
  stripValueDefault: number;
  numberOfChainages: number;
  totalBuildUpValue: number;
}

// Column definitions for road types
const roadTypeColumns: ExcelColumn[] = [
  { key: "name", title: "Road Type Name" },
  { key: "surface", title: "Surface Type" },
  { key: "surfaceCourse.type", title: "Surface Course Type" },
  {
    key: "surfaceCourse.thickness",
    title: "Surface Thickness (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "binderCourse.thickness",
    title: "Binder Thickness (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "baseCourse.thickness",
    title: "Base Thickness (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  { key: "subBase.type", title: "Sub-base Type" },
  {
    key: "subBase.thickness",
    title: "Sub-base Thickness (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "capping",
    title: "Capping (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "totalConstructionThickness",
    title: "Total Thickness (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
];

// Column definitions for cut/fill sections
const cutFillSectionColumns: ExcelColumn[] = [
  { key: "chainage", title: "Chainage" },
  { key: "carriagewayFootwayId", title: "Carriageway/Footway ID" },
  { key: "roadTypeId", title: "Road Type ID" },
  {
    key: "area",
    title: "Area (m²)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "frl",
    title: "FRL (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "roadFormation",
    title: "Road Formation (m²)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "existingGroundLevel",
    title: "Existing Ground Level (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "stripValue",
    title: "Strip Value (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "formationLevel",
    title: "Formation Level (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "depth",
    title: "Depth (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "roadArea",
    title: "Road Area (m²)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "cappingArea",
    title: "Capping Area (m²)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "cutVolume",
    title: "Cut Volume (m³)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "fillVolume",
    title: "Fill Volume (m³)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "fpl",
    title: "FPL (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "pathFormLevel",
    title: "Path Form Level (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "footpathsDepth",
    title: "Footpaths Depth (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "pathArea",
    title: "Path Area (m²)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "footpathsCutVolume",
    title: "Footpaths Cut (m³)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "footpathsFillVolume",
    title: "Footpaths Fill (m³)",
    render: (value) => Number(value || 0).toFixed(2),
  },
];

// Column definitions for carriageway & footways
const carriagewayFootwayColumns: ExcelColumn[] = [
  { key: "roadName", title: "Road Name" },
  { key: "tarmacSpec1", title: "Tarmac Spec 1 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tarmacSpec2", title: "Tarmac Spec 2 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tarmacSpec3", title: "Tarmac Spec 3 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tarmacSpec4", title: "Tarmac Spec 4 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "blockPavedSpec1", title: "Block Paved Spec 1 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "blockPavedSpec2", title: "Block Paved Spec 2 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "blockPavedSpec3", title: "Block Paved Spec 3 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "blockPavedSpec4", title: "Block Paved Spec 4 (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "totalRoadArea", title: "Total Road Area (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "kerbRace", title: "Kerb Race (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "dowelBarsInRace", title: "Dowel Bars in Race (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "keyChannel", title: "Key Channel (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "filletToCrossover", title: "Fillet to Crossover (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "removeFillet", title: "Remove Fillet (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "extrudedKerbRace", title: "Extruded Kerb Race (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tempKerbBlock", title: "Temp Kerb / Block (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "removeTempKerbBlock", title: "Remove Temp Kerb / Block (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "kerb10x5IncRace", title: "10x5 Kerb (inc Race) All Sizes (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "kerb10x5OnRace", title: "10x5 Kerb (on Race) (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "kerb6x5OnRace", title: "6x5 Kerb (on Race) (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "dropKerb", title: "Drop Kerb (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "delineationKerb", title: "Delineation Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "eoRadiusKerb", title: "EO Radius Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "eoRadiusDropKerb", title: "EO Radius Drop Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "kasselKerb", title: "Kassel Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "triefKerb", title: "Trief Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "beanyKerb", title: "Beany Kerb (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "beanyGully", title: "Beany Gully (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "acoKerbDrain", title: "ACO Kerb Drain (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "acoKerbGully", title: "ACO Kerb Gully (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "eoRamps", title: "EO Ramps (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "tarmacPath", title: "Tarmac Path (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tarmacCrossover", title: "Tarmac Crossover (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "heavyDutyCrossover", title: "Heavy Duty Crossover (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "blockPavedPathCrossover", title: "Block Paved Path / Crossover (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "cycleway", title: "Cycleway (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "verge", title: "Verge (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "totalPath", title: "Total Path (m²)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "edgings150x50", title: "150x50 Edgings (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "edgings200x50", title: "200x50 Edgings (m)", render: (v) => Number(v || 0).toFixed(2) },
  { key: "tactilePaving", title: "Tactile Paving (Nr)", render: (v) => Number(v || 0).toFixed(0) },
  { key: "corduroyPaving", title: "Corduroy Paving (Nr)", render: (v) => Number(v || 0).toFixed(0) },
];

// Column definitions for footway configurations
const footwayConfigurationColumns: ExcelColumn[] = [
  { key: "name", title: "Footway Type" },
  {
    key: "surface",
    title: "Surface (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "binder",
    title: "Binder (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "base",
    title: "Base (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "type1",
    title: "Type 1 (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "total",
    title: "Total (m)",
    render: (value) => Number(value || 0).toFixed(2),
  },
];

// Export roads data to Excel
export const exportRoadsToExcel = async (
  data: RoadsExcelData,
  projectName: string
): Promise<void> => {
  const sheets = [
    {
      name: "Road Types",
      data: data.roadTypes,
      columns: roadTypeColumns,
    },
    {
      name: "Carriageway & Footways",
      data: data.carriagewayFootways,
      columns: carriagewayFootwayColumns,
    },
    {
      name: "Footway Configurations",
      data: data.footwayConfigurations,
      columns: footwayConfigurationColumns,
    },
    {
      name: "Cut Fill Sections",
      data: data.cutFillSections,
      columns: cutFillSectionColumns,
    },
  ];

  // Add summary sheet with configuration data
  const summaryData = [
    {
      Field: "Strip Value Default (m)",
      Value: data.stripValueDefault?.toFixed(2) || "0.000",
    },
    {
      Field: "Number of Chainages",
      Value: data.numberOfChainages?.toString() || "10",
    },
    {
      Field: "Total BuildUp Value (m³)",
      Value: data.totalBuildUpValue?.toFixed(2) || "0.000",
    },
    {
      Field: "Capping Layer Enabled",
      Value: data.cappingLayer?.enabled ? "Yes" : "No",
    },
    {
      Field: "Capping Layer Thicknesses (mm)",
      Value: data.cappingLayer?.thicknesses?.join(", ") || "None",
    },
  ];

  sheets.push({
    name: "Configuration",
    data: summaryData,
    columns: [
      { key: "Field", title: "Configuration Field" },
      { key: "Value", title: "Value" },
    ],
  });

  await exportToExcelMultiSheet({
    filename: `${projectName}_Roads_Export`,
    includeBranding: true,
    projectName: projectName,
    sheets: sheets,
  });
};

// Import roads data from Excel
export const importRoadsFromExcel = async (
  file: File
): Promise<{
  data: RoadsExcelData;
  errors: string[];
  warnings: string[];
}> => {
  const options = {
    expectedSheets: [
      {
        name: "Road Types",
        expectedColumns: roadTypeColumns.map((col) => col.title),
      },
      {
        name: "Carriageway & Footways",
        expectedColumns: carriagewayFootwayColumns.map((col) => col.title),
      },
      {
        name: "Footway Configurations",
        expectedColumns: footwayConfigurationColumns.map((col) => col.title),
      },
      {
        name: "Cut Fill Sections",
        expectedColumns: cutFillSectionColumns.map((col) => col.title),
      },
      {
        name: "Configuration",
        expectedColumns: ["Configuration Field", "Value"],
      },
    ],
    preserveEmptyValues: true,
  };
  const result = await importFromExcelMultiSheet(file, options);

  const data: RoadsExcelData = {
    roadTypes: [],
    cutFillSections: [],
    carriagewayFootways: [],
    footwayConfigurations: [],
    cappingLayer: { enabled: false, thicknesses: [] },
    stripValueDefault: 0.3,
    numberOfChainages: 10,
    totalBuildUpValue: 0,
  };

  const errors: string[] = [];
  const warnings: string[] = [];

  // Add global errors
  errors.push(...result.globalErrors);

  console.log(
    "Import result sheets:",
    result.sheets.map((s) => ({ name: s.name, dataLength: s.data.length }))
  );

  // Process each sheet
  result.sheets.forEach((sheet) => {
    try {
      console.log(
        `Processing sheet: ${sheet.name} with ${sheet.data.length} rows`
      );

      switch (sheet.name) {
        case "Road Types":
          if (sheet.data.length > 0) {
            data.roadTypes = transformRoadTypeData(sheet.data);
            console.log(`Imported ${data.roadTypes.length} road types`);
          }
          break;
        case "Carriageway & Footways":
          if (sheet.data.length > 0) {
            data.carriagewayFootways = transformCarriagewayFootwayData(
              sheet.data
            );
            console.log(
              `Imported ${data.carriagewayFootways.length} carriageway & footways`
            );
          }
          break;
        case "Footway Configurations":
          if (sheet.data.length > 0) {
            data.footwayConfigurations = transformFootwayConfigurationData(
              sheet.data
            );
            console.log(
              `Imported ${data.footwayConfigurations.length} footway configurations`
            );
          }
          break;
        case "Cut Fill Sections":
          if (sheet.data.length > 0) {
            data.cutFillSections = transformCutFillSectionData(sheet.data);
            console.log(
              `Imported ${data.cutFillSections.length} cut fill sections`
            );
          }
          break;
        case "Configuration":
          if (sheet.data.length > 0) {
            processConfigurationData(sheet.data, data);
            console.log("Processed configuration data");
          }
          break;
        default:
          warnings.push(`Unknown sheet: ${sheet.name}`);
      }

      // Add sheet-specific errors and warnings
      errors.push(...sheet.errors);
      warnings.push(...sheet.warnings);
    } catch (error) {
      errors.push(`Error processing sheet ${sheet.name}: ${error.message}`);
    }
  });

  return {
    data,
    errors,
    warnings,
  };
};

// Transform functions for each data type
const transformRoadTypeData = (rawData: any[]): RoadType[] => {
  return rawData.map((row: any, index: number) => ({
    id: row.id || `imported_rt_${Date.now()}_${index}`,
    name: String(row["Road Type Name"] || row.name || `Road Type ${index + 1}`),
    surface: String(row["Surface Type"] || row.surface || "Tarmac Road"),
    surfaceCourse: {
      type: String(
        row["Surface Course Type"] || row["surfaceCourse.type"] || "HRA 55/10"
      ),
      thickness: Number(
        row["Surface Thickness (m)"] || row["surfaceCourse.thickness"] || 0
      ),
    },
    binderCourse: {
      thickness: Number(
        row["Binder Thickness (m)"] || row["binderCourse.thickness"] || 0
      ),
    },
    baseCourse: {
      thickness: Number(
        row["Base Thickness (m)"] || row["baseCourse.thickness"] || 0
      ),
    },
    subBase: {
      type: String(row["Sub-base Type"] || row["subBase.type"] || "Type 1"),
      thickness: Number(
        row["Sub-base Thickness (m)"] || row["subBase.thickness"] || 0
      ),
    },
    capping: Number(row["Capping (m)"] || row.capping || 0),
    totalConstructionThickness: Number(
      row["Total Thickness (m)"] || row.totalConstructionThickness || 0
    ),
  }));
};

const transformCarriagewayFootwayData = (
  rawData: any[]
): CarriagewayFootway[] => {
  return rawData.map((row: any, index: number) => {
    const n = (col: string, fallback: string) =>
      Number(row[col] || row[fallback] || 0);
    const tarmacSpec1 = n("Tarmac Spec 1 (m²)", "tarmacSpec1");
    const tarmacSpec2 = n("Tarmac Spec 2 (m²)", "tarmacSpec2");
    const tarmacSpec3 = n("Tarmac Spec 3 (m²)", "tarmacSpec3");
    const tarmacSpec4 = n("Tarmac Spec 4 (m²)", "tarmacSpec4");
    const blockPavedSpec1 = n("Block Paved Spec 1 (m²)", "blockPavedSpec1");
    const blockPavedSpec2 = n("Block Paved Spec 2 (m²)", "blockPavedSpec2");
    const blockPavedSpec3 = n("Block Paved Spec 3 (m²)", "blockPavedSpec3");
    const blockPavedSpec4 = n("Block Paved Spec 4 (m²)", "blockPavedSpec4");
    const tarmacPath = n("Tarmac Path (m²)", "tarmacPath");
    const tarmacCrossover = n("Tarmac Crossover (m²)", "tarmacCrossover");
    const heavyDutyCrossover = n("Heavy Duty Crossover (m²)", "heavyDutyCrossover");
    const blockPavedPathCrossover = n("Block Paved Path / Crossover (m²)", "blockPavedPathCrossover");
    const cycleway = n("Cycleway (m²)", "cycleway");
    const verge = n("Verge (m²)", "verge");
    return {
      id: row.id || `imported_cf_${Date.now()}_${index}`,
      roadName: String(row["Road Name"] || row.roadName || `Road ${index + 1}`),
      tarmacSpec1, tarmacSpec2, tarmacSpec3, tarmacSpec4,
      blockPavedSpec1, blockPavedSpec2, blockPavedSpec3, blockPavedSpec4,
      totalRoadArea:
        Number(row["Total Road Area (m²)"] || row.totalRoadArea || 0) ||
        tarmacSpec1 + tarmacSpec2 + tarmacSpec3 + tarmacSpec4 +
        blockPavedSpec1 + blockPavedSpec2 + blockPavedSpec3 + blockPavedSpec4,
      kerbRace: n("Kerb Race (m)", "kerbRace"),
      dowelBarsInRace: n("Dowel Bars in Race (Nr)", "dowelBarsInRace"),
      keyChannel: n("Key Channel (m)", "keyChannel"),
      filletToCrossover: n("Fillet to Crossover (m)", "filletToCrossover"),
      removeFillet: n("Remove Fillet (m)", "removeFillet"),
      extrudedKerbRace: n("Extruded Kerb Race (m)", "extrudedKerbRace"),
      tempKerbBlock: n("Temp Kerb / Block (m)", "tempKerbBlock"),
      removeTempKerbBlock: n("Remove Temp Kerb / Block (m)", "removeTempKerbBlock"),
      kerb10x5IncRace: n("10x5 Kerb (inc Race) All Sizes (m)", "kerb10x5IncRace"),
      kerb10x5OnRace: n("10x5 Kerb (on Race) (m)", "kerb10x5OnRace"),
      kerb6x5OnRace: n("6x5 Kerb (on Race) (m)", "kerb6x5OnRace"),
      dropKerb: n("Drop Kerb (Nr)", "dropKerb"),
      delineationKerb: n("Delineation Kerb (m)", "delineationKerb"),
      eoRadiusKerb: n("EO Radius Kerb (m)", "eoRadiusKerb"),
      eoRadiusDropKerb: n("EO Radius Drop Kerb (m)", "eoRadiusDropKerb"),
      kasselKerb: n("Kassel Kerb (m)", "kasselKerb"),
      triefKerb: n("Trief Kerb (m)", "triefKerb"),
      beanyKerb: n("Beany Kerb (m)", "beanyKerb"),
      beanyGully: n("Beany Gully (Nr)", "beanyGully"),
      acoKerbDrain: n("ACO Kerb Drain (m)", "acoKerbDrain"),
      acoKerbGully: n("ACO Kerb Gully (Nr)", "acoKerbGully"),
      eoRamps: n("EO Ramps (Nr)", "eoRamps"),
      tarmacPath, tarmacCrossover, heavyDutyCrossover, blockPavedPathCrossover, cycleway, verge,
      totalPath:
        Number(row["Total Path (m²)"] || row.totalPath || 0) ||
        tarmacPath + tarmacCrossover + heavyDutyCrossover + blockPavedPathCrossover + cycleway + verge,
      edgings150x50: n("150x50 Edgings (m)", "edgings150x50"),
      edgings200x50: n("200x50 Edgings (m)", "edgings200x50"),
      tactilePaving: n("Tactile Paving (Nr)", "tactilePaving"),
      corduroyPaving: n("Corduroy Paving (Nr)", "corduroyPaving"),
    };
  });
};

const transformFootwayConfigurationData = (
  rawData: any[]
): FootwayConfiguration[] => {
  return rawData.map((row: any, index: number) => ({
    id: row.id || `imported_fc_${Date.now()}_${index}`,
    name: String(row["Footway Type"] || row.name || "Tarmac"),
    surface: Number(row["Surface (m)"] || row.surface || 0),
    binder: Number(row["Binder (m)"] || row.binder || 0),
    base: Number(row["Base (m)"] || row.base || 0),
    type1: Number(row["Type 1 (m)"] || row.type1 || 0),
    total: Number(row["Total (m)"] || row.total || 0),
  }));
};

const transformCutFillSectionData = (rawData: any[]): CutFillSection[] => {
  return rawData.map((row: any, index: number) => ({
    id: row.id || `imported_cfs_${Date.now()}_${index}`,
    chainage: String(row["Chainage"] || row.chainage || "0"),
    carriagewayFootwayId: String(
      row["Carriageway/Footway ID"] || row.carriagewayFootwayId || ""
    ),
    roadTypeId: String(row["Road Type ID"] || row.roadTypeId || ""),
    area: Number(row["Area (m²)"] || row.area || 0),
    frl: Number(row["FRL (m)"] || row.frl || 0),
    finishedRoadLevel: Number(row["FRL (m)"] || row.frl || 0),
    existingGroundLevel: Number(
      row["Existing Ground Level (m)"] || row.existingGroundLevel || 0
    ),
    stripValue: Number(row["Strip Value (m)"] || row.stripValue || 0.3),
    topsoilStrip: Number(row.topsoilStrip || 0),
    lessStrip: Number(row.lessStrip || 0),
    strippedLevel: Number(row.strippedLevel || 0),
    depth: Number(row["Depth (m)"] || row.depth || 0),
    cutFillDepth: Number(row.cutFillDepth || 0),
    roadConstructionThickness: Number(row.roadConstructionThickness || 0),
    formationLevel: Number(
      row["Formation Level (m)"] || row.formationLevel || 0
    ),
    roadFormation: Number(row["Road Formation (m²)"] || row.roadFormation || 0),
    roadArea: Number(row["Road Area (m²)"] || row.roadArea || 0),
    cappingArea: Number(row["Capping Area (m²)"] || row.cappingArea || 0),
    cutVolume: Number(row["Cut Volume (m³)"] || row.cutVolume || 0),
    fillVolume: Number(row["Fill Volume (m³)"] || row.fillVolume || 0),
    fpl: Number(row["FPL (m)"] || row.fpl || 0),
    pathFormLevel: Number(row["Path Form Level (m)"] || row.pathFormLevel || 0),
    footpathsLessStrip: Number(row.footpathsLessStrip || 0),
    footpathsDepth: Number(
      row["Footpaths Depth (m)"] || row.footpathsDepth || 0
    ),
    footpathsCutVolume: Number(
      row["Footpaths Cut (m³)"] || row.footpathsCutVolume || 0
    ),
    footpathsFillVolume: Number(
      row["Footpaths Fill (m³)"] || row.footpathsFillVolume || 0
    ),
    pathArea: Number(row["Path Area (m²)"] || row.pathArea || 0),
  }));
};

const processConfigurationData = (
  rawData: any[],
  data: RoadsExcelData
): void => {
  rawData.forEach((row: any) => {
    const field = String(row["Configuration Field"] || row.Field || "");
    const value = row["Value"] || row.value;

    switch (field) {
      case "Strip Value Default (m)":
        data.stripValueDefault = Number(value) || 0.3;
        break;
      case "Number of Chainages":
        data.numberOfChainages = Number(value) || 10;
        break;
      case "Total BuildUp Value (m³)":
        data.totalBuildUpValue = Number(value) || 0;
        break;
      case "Capping Layer Enabled":
        data.cappingLayer.enabled = String(value).toLowerCase() === "yes";
        break;
      case "Capping Layer Thicknesses (mm)":
        if (value && String(value) !== "None") {
          data.cappingLayer.thicknesses = String(value)
            .split(",")
            .map((t: string) => Number(t.trim()))
            .filter((t: number) => !isNaN(t));
        }
        break;
    }
  });
};

// Generate roads template
export const generateRoadsTemplate = async (
  projectName: string
): Promise<void> => {
  const sheets = [
    {
      name: "Road Types",
      columns: roadTypeColumns,
    },
    {
      name: "Carriageway & Footways",
      columns: carriagewayFootwayColumns,
    },
    {
      name: "Footway Configurations",
      columns: footwayConfigurationColumns,
    },
    {
      name: "Cut Fill Sections",
      columns: cutFillSectionColumns,
    },
    {
      name: "Configuration",
      columns: [
        { key: "Field", title: "Configuration Field" },
        { key: "Value", title: "Value" },
      ],
    },
  ];

  await generateMultiSheetTemplate(`${projectName}_Roads_Template`, sheets);
};

// Parse 'Carriageway Take Off' sheet and return RoadType[] (8 road types)
// Sheet layout: labels in col 2, names in row 2 cols 3–10, data in rows 4–13 cols 3–10
export const parseCarriagewayTakeoffRoadTypes = async (
  file: File
): Promise<{ roadTypes: RoadType[]; cappingType: CappingType }> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) return { roadTypes: [], cappingType: 'R/C 6F2 capping' };

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // Sheet structure (confirmed from console.table output):
  // Row 2: road type names in cols 3–10
  // Row 4+: label in col 2, values in cols 3–10
  // Row 10: empty label but contains surface type strings (follows "Surface" row)
  const COL_LABEL = 2;   // column with row labels
  const COL_START = 3;   // first data column (Tarmac Spec 1)
  const COL_END = 10;    // last data column (Block Paved Spec 4)
  const NAME_ROW = 2;    // row containing road type names
  const DATA_START = 4;  // first data row
  const DATA_END = 13;   // last data row

  // IDs must match DEFAULT_ROAD_TYPES so mergedRoadTypes picks them up
  const FIXED_IDS = ['tarmac-1', 'tarmac-2', 'tarmac-3', 'tarmac-4', 'block-1', 'block-2', 'block-3', 'block-4'];

  const DEFAULT_NAMES = [
    'Tarmac Spec 1', 'Tarmac Spec 2', 'Tarmac Spec 3', 'Tarmac Spec 4',
    'Block Paved Spec 1', 'Block Paved Spec 2', 'Block Paved Spec 3', 'Block Paved Spec 4',
  ];

  const nameRow = (raw[NAME_ROW] ?? []) as unknown[];
  const roadTypeNames = Array.from({ length: COL_END - COL_START + 1 }, (_, i) => {
    const v = String(nameRow[COL_START + i] ?? '').trim();
    return v || DEFAULT_NAMES[i] || `Road Type ${i + 1}`;
  });

  const roadTypes: RoadType[] = roadTypeNames.map((name, i) => ({
    id: FIXED_IDS[i],
    name,
    surface: i < 4 ? 'Tarmac Road' : 'Block Paved Road',
    membrane: false,
    surfaceCourse: { type: i < 4 ? 'HRA 55/10' : '200x100x60mm Keybloc Paving', thickness: 0 },
    binderCourse: { thickness: 0 },
    baseCourse: { thickness: 0 },
    subBase: { type: 'Type 1' as const, thickness: 0 },
    capping: 0,
    eoPreCoat: false,
    eoPSV65: false,
    sandPave: 0,
    totalConstructionThickness: 0,
  }));

  let cappingType: CappingType = 'R/C 6F2 capping';
  let prevLabel = '';

  for (let rowIdx = DATA_START; rowIdx <= Math.min(DATA_END, raw.length - 1); rowIdx++) {
    const row = (raw[rowIdx] ?? []) as unknown[];
    const label = String(row[COL_LABEL] ?? '').trim().toLowerCase();

    const isCappingRow = label.includes('capping') || label.includes('6f2') || label.includes('6f5')
      || label.includes('w75') || label.includes('w150') || label.startsWith('r/c') || label.startsWith('q ');
    if (isCappingRow) {
      if (label.includes('w75') || label.includes('w150')) {
        cappingType = 'W75/W150';
      } else if (label.includes('q 6f5') || label.includes('q6f5')) {
        cappingType = 'Q 6F5 capping';
      } else {
        cappingType = 'R/C 6F2 capping';
      }
    }

    for (let c = COL_START; c <= COL_END; c++) {
      const rt = roadTypes[c - COL_START];
      const cell = row[c];
      const num = () => parseFloat(String(cell ?? 0)) || 0;
      const str = () => String(cell ?? '').trim();
      const bool = () => String(cell ?? '').toLowerCase().trim() === 'yes';

      if (label.includes('membrane')) {
        rt.membrane = bool();
      } else if (isCappingRow) {
        rt.capping = num();
      } else if (label.includes('sub') && label.includes('base')) {
        rt.subBase = { ...rt.subBase, thickness: num() };
      } else if (label === 'base' || (label.includes('base') && !label.includes('sub') && !label.includes('surface') && !label.includes('course'))) {
        rt.baseCourse = { thickness: num() };
      } else if (label.includes('binder')) {
        rt.binderCourse = { thickness: num() };
      } else if (label === 'surface' || (label.includes('surface') && !label.includes('type') && !label.includes('course'))) {
        rt.surfaceCourse = { ...rt.surfaceCourse, thickness: num() };
      } else if (label === '' && prevLabel === 'surface' && str()) {
        // Row 10: empty label, follows "Surface" row — contains surface type strings
        rt.surfaceCourse = { ...rt.surfaceCourse, type: str() };
      } else if (label.includes('pre') && label.includes('coat')) {
        rt.eoPreCoat = bool();
      } else if (label.includes('psv')) {
        rt.eoPSV65 = bool();
      } else if (label.includes('sand') || label.includes('pave')) {
        rt.sandPave = num();
      }
    }

    if (label) prevLabel = label;
  }

  console.group('📋 parseCarriagewayTakeoffRoadTypes — result');
  console.table(roadTypes.map(rt => ({
    id: rt.id, name: rt.name, surface: rt.surface,
    membrane: rt.membrane, capping: rt.capping,
    'subBase.t': rt.subBase?.thickness, 'base.t': rt.baseCourse?.thickness,
    'binder.t': rt.binderCourse?.thickness, 'surface.t': rt.surfaceCourse?.thickness,
    'surface.type': rt.surfaceCourse?.type, eoPreCoat: rt.eoPreCoat, eoPSV65: rt.eoPSV65,
    sandPave: rt.sandPave,
  })));
  console.log('Capping Type:', cappingType);
  console.groupEnd();

  return { roadTypes, cappingType };
};

// Read 'Carriageway Take Off' sheet and console log all raw data
export const logCarriagewayTakeoffSheet = async (file: File): Promise<void> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellStyles: true, cellFormula: true, cellNF: true, raw: true });

  console.log('📂 All sheet names in workbook:', workbook.SheetNames);

  // Try all common name variants
  const candidates = [
    'Carriageway Take Off',
    'Carriageway Take off',
    'Carriageway Takeoff',
    'Carriageway take off',
    'CARRIAGEWAY TAKE OFF',
  ];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('❌ No "Carriageway Take Off" sheet found. Available:', workbook.SheetNames);
    return;
  }

  const ws = workbook.Sheets[sheetName];

  // Raw array-of-arrays — every cell, no header parsing
  const rawArrays = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];
  console.group(`📋 Sheet: "${sheetName}" — ${rawArrays.length} row(s) (raw arrays)`);
  console.log(rawArrays);
  console.groupEnd();

  // Also as objects using row 0 as headers
  if (rawArrays.length > 1) {
    const headers = (rawArrays[0] as string[]).map((h) => String(h ?? '').trim());
    const rows = rawArrays.slice(1).map((row) => {
      const obj: Record<string, unknown> = {};
      headers.forEach((h, i) => { obj[h || `col_${i}`] = (row as unknown[])[i]; });
      return obj;
    });
    console.group(`📋 Sheet: "${sheetName}" — parsed as objects`);
    console.table(rows);
    console.groupEnd();
  }
};

// Parse footpaths config from 'Carriageway Take Off' sheet
// Layout: label in BI (col 60), data in BJ–BO (cols 61–66), rows ExcelRow 4–11 (0-indexed 3–10)
// Columns map to: BJ=tarmacPath, BK=tarmacCrossover, BL=heavyDutyCrossover, BM=blockPavedCrossover, BN=cycleway, BO=verge
export const parseFootpathsConfig = async (file: File): Promise<FootpathsConstructionConfig | null> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) return null;

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  const COL_LABEL = 60;  // BI
  const COL_START = 61;  // BJ (Tarmac Path)
  const COL_END = 66;    // BO (Verge)
  const DATA_START = 3;  // ExcelRow 4 (0-indexed)
  const DATA_END = 10;   // ExcelRow 11 (0-indexed), Topsoil row

  const KEYS: Array<FootpathColumnKey> = [
    'tarmacPath', 'tarmacCrossover', 'heavyDutyCrossover', 'blockPavedCrossover', 'cycleway', 'verge',
  ];

  const VALID_TYPES = new Set<string>([
    'AC6 DBM', 'AC10 DBM', 'HRA', 'SMA',
    '60mm Block Paving', '80mm Block Paving', '60mm Tegula Paving', '80mm Tegula Paving',
    '60mm Keyblok', '80mm Keyblok', 'site won topsoil', 'imported topsoil',
  ]);

  // Base/Binder/Surface are now fixed-option dropdowns in FootpathsConfigModal.tsx
  // (see BASE/BINDER/SURFACE Select.Option values there) rather than free-numeric
  // inputs — snap whatever the sheet contains to the nearest valid option so an
  // imprecise/rounded cell value (e.g. 0.076) doesn't leave the dropdown showing
  // blank. 0 stays 0 (unset/"Select"), not snapped to the nearest nonzero option.
  const BASE_OPTIONS = [0.1, 0.15];
  const BINDER_OPTIONS = [0.05, 0.06, 0.075, 0.08, 0.1];
  const SURFACE_OPTIONS = [0.02, 0.025, 0.03];
  const snapToNearest = (value: number, options: number[]): number => {
    if (value <= 0) return 0;
    return options.reduce((closest, opt) => (Math.abs(opt - value) < Math.abs(closest - value) ? opt : closest), options[0]);
  };

  const DEFAULT_TYPES: Record<FootpathColumnKey, FootpathConstructionType> = {
    tarmacPath: 'AC6 DBM',
    tarmacCrossover: 'AC6 DBM',
    heavyDutyCrossover: 'AC6 DBM',
    blockPavedCrossover: '60mm Keyblok',
    cycleway: 'AC6 DBM',
    verge: 'site won topsoil',
  };

  const cols: Record<FootpathColumnKey, FootpathLayerConfig> = {} as Record<FootpathColumnKey, FootpathLayerConfig>;
  KEYS.forEach((key) => {
    cols[key] = {
      membrane: false,
      capping: 0,
      subBase: 0,
      base: 0,
      binder: 0,
      surface: 0,
      constructionType: DEFAULT_TYPES[key],
      topsoil: 0,
    };
  });

  let prevLabel = '';

  for (let rowIdx = DATA_START; rowIdx <= Math.min(DATA_END, raw.length - 1); rowIdx++) {
    const row = (raw[rowIdx] ?? []) as unknown[];
    const label = String(row[COL_LABEL] ?? '').trim().toLowerCase();

    for (let c = COL_START; c <= COL_END; c++) {
      const key = KEYS[c - COL_START];
      const col = cols[key];
      const cell = row[c];
      const num = () => parseFloat(String(cell ?? 0)) || 0;
      const str = () => String(cell ?? '').trim();
      const bool = () => String(cell ?? '').toLowerCase().trim() === 'yes';

      if (label.includes('membrane')) {
        col.membrane = bool();
      } else if (label.includes('capping')) {
        col.capping = num();
      } else if (label.includes('sub')) {
        col.subBase = num();
      } else if (label === 'base' || (label.includes('base') && !label.includes('sub'))) {
        col.base = snapToNearest(num(), BASE_OPTIONS);
      } else if (label.includes('binder')) {
        col.binder = snapToNearest(num(), BINDER_OPTIONS);
      } else if (label === 'surface') {
        col.surface = snapToNearest(num(), SURFACE_OPTIONS);
      } else if (label === '' && prevLabel === 'surface' && str() && VALID_TYPES.has(str())) {
        col.constructionType = str() as FootpathConstructionType;
      } else if (label.includes('topsoil')) {
        col.topsoil = num();
      }
    }

    if (label) prevLabel = label;
  }

  console.group('📋 parseFootpathsConfig — result');
  console.table(KEYS.map((key) => ({ key, ...cols[key] })));
  console.groupEnd();

  return cols as FootpathsConstructionConfig;
};

// Log footpaths config section from 'Carriageway Take Off' sheet
// Footpaths data: cols BI–BO (0-indexed 60–66), rows 3–12 (Excel 1-indexed = 0-indexed 2–11)
export const logFootpathsConfigData = async (file: File): Promise<void> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('❌ No "Carriageway Take Off" sheet found');
    return;
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // BI=60, BJ=61, BK=62, BL=63, BM=64, BN=65, BO=66 (0-indexed)
  const COL_START = 60; // BI (label column)
  const ROW_START = 2;  // Excel row 3 (0-indexed)
  const ROW_END = 11;   // Excel row 12 (0-indexed)

  const colLetters = ['BI', 'BJ', 'BK', 'BL', 'BM', 'BN', 'BO'];

  console.group(`📋 Footpaths Config — sheet "${sheetName}", cols BI–BO, rows 3–12`);
  const debugRows = Array.from(
    { length: Math.min(ROW_END - ROW_START + 1, raw.length - ROW_START) },
    (_, i) => {
      const rowIdx = ROW_START + i;
      const r = (raw[rowIdx] ?? []) as unknown[];
      const obj: Record<string, unknown> = { rowIdx, 'ExcelRow': rowIdx + 1, label_colA: r[0], label_colB: r[1] };
      colLetters.forEach((letter, ci) => {
        obj[letter] = r[COL_START + ci];
      });
      return obj;
    }
  );
  console.table(debugRows);
  console.groupEnd();
};

// Maps sheet column header text (lowercase) → CarriagewayFootway field key
const SHEET_HEADER_TO_FIELD: Partial<Record<string, keyof CarriagewayFootway>> = {
  // Carriageway
  'tarmac spec 1': 'tarmacSpec1',
  'tarmac spec 2': 'tarmacSpec2',
  'tarmac spec 3': 'tarmacSpec3',
  'tarmac spec 4': 'tarmacSpec4',
  'block paved spec 1': 'blockPavedSpec1',
  'block paved spec 2': 'blockPavedSpec2',
  'block paved spec 3': 'blockPavedSpec3',
  'block paved spec 4': 'blockPavedSpec4',
  'total road area': 'totalRoadArea',
  'kerb race': 'kerbRace',
  'dowel bars in race': 'dowelBarsInRace',
  'dowel bars in race (nr)': 'dowelBarsInRace',
  'key channel': 'keyChannel',
  'fillet to crossover': 'filletToCrossover',
  'remove fillet': 'removeFillet',
  'extruded kerb race': 'extrudedKerbRace',
  'temp kerb / block': 'tempKerbBlock',
  'temp kerb/block': 'tempKerbBlock',
  'remove temp kerb / block': 'removeTempKerbBlock',
  'remove temp kerb/block': 'removeTempKerbBlock',
  '10x5 kerb (inc race) all sizes': 'kerb10x5IncRace',
  '10x5 kerb inc race all sizes': 'kerb10x5IncRace',
  '10x5 kerb (on race)': 'kerb10x5OnRace',
  '10x5 kerb on race': 'kerb10x5OnRace',
  '6x5 kerb (on race)': 'kerb6x5OnRace',
  '6x5 kerb on race': 'kerb6x5OnRace',
  'drop kerb': 'dropKerb',
  'delineation kerb': 'delineationKerb',
  'eo radius kerb': 'eoRadiusKerb',
  'eo radius drop kerb': 'eoRadiusDropKerb',
  'kassel kerb': 'kasselKerb',
  'trief kerb': 'triefKerb',
  'beany kerb': 'beanyKerb',
  'beany gully': 'beanyGully',
  'aco kerb drain': 'acoKerbDrain',
  'aco kerb gully': 'acoKerbGully',
  'eo ramps': 'eoRamps',
  // Footways
  'tarmac path': 'tarmacPath',
  'tarmac crossover': 'tarmacCrossover',
  'heavy duty crossover': 'heavyDutyCrossover',
  'block paved path / crossover': 'blockPavedPathCrossover',
  'block paved path crossover': 'blockPavedPathCrossover',
  'block paved & crossover': 'blockPavedPathCrossover',
  'cycleway': 'cycleway',
  'verge': 'verge',
  'total path': 'totalPath',
  '150x50 edgings': 'edgings150x50',
  '200x50 edgings': 'edgings200x50',
  'tactile paving': 'tactilePaving',
  'corduroy paving': 'corduroyPaving',
};

// Positional fallback: col offset from DATA_COL_START (2) → field.
// null at position 31 = the one empty gap column between carriageway and footways sections.
const POSITIONAL_FIELDS: Array<keyof CarriagewayFootway | null> = [
  'tarmacSpec1', 'tarmacSpec2', 'tarmacSpec3', 'tarmacSpec4',
  'blockPavedSpec1', 'blockPavedSpec2', 'blockPavedSpec3', 'blockPavedSpec4',
  'totalRoadArea', 'kerbRace', 'dowelBarsInRace', 'keyChannel',
  'filletToCrossover', 'removeFillet', 'extrudedKerbRace', 'tempKerbBlock',
  'removeTempKerbBlock', 'kerb10x5IncRace', 'kerb10x5OnRace', 'kerb6x5OnRace',
  'dropKerb', 'delineationKerb', 'eoRadiusKerb', 'eoRadiusDropKerb',
  'kasselKerb', 'triefKerb', 'beanyKerb', 'beanyGully',
  'acoKerbDrain', 'acoKerbGully', 'eoRamps',
  null, // empty gap column between carriageway and footways
  'tarmacPath', 'tarmacCrossover', 'heavyDutyCrossover', 'blockPavedPathCrossover',
  'cycleway', 'verge', 'totalPath',
  'edgings150x50', 'edgings200x50', 'tactilePaving', 'corduroyPaving',
];

// Parse carriageway road data and footways from 'Carriageway Take Off' sheet.
// Each road has two rows with the road name in col B (index 1):
//   Row A: column headers in c2+ (or empty for Road 1)
//   Row B: data values in c2+
// The data row is always the SECOND occurrence of each road name in col B.
export const parseCarriagewayAndFootwaysData = async (file: File): Promise<CarriagewayFootway[]> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) return [];

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  const SECTION_HEADERS = new Set(['carriageways', 'footways', 'roads']);
  const DATA_COL_START = 2; // values start at column index 2
  // Upper bound for scanning columns — some rows' arrays get truncated by
  // SheetJS at their last non-empty cell, which would otherwise cut off the
  // trailing Footpaths/Kerbs/Edgings columns whenever a road's header or data
  // row happens to have no content past a certain point.
  const COL_SCAN_END = DATA_COL_START + POSITIONAL_FIELDS.length;

  // Build a global column-index → field map by scanning EVERY header row
  // found (not just the first one) and merging the results. A single road's
  // header row not repeating every column's label (e.g. blank cells past the
  // Kerbs/Footpaths columns) must not prevent other roads' header rows from
  // filling in those columns.
  const colIndexToField = new Map<number, keyof CarriagewayFootway>();
  for (const rawRow of raw) {
    const row = rawRow as unknown[];
    const c1 = String(row[1] ?? '').trim();
    const c2 = String(row[2] ?? '').trim();
    if (!c1 || SECTION_HEADERS.has(c1.toLowerCase())) continue;
    if (c2 && isNaN(Number(c2))) {
      // This is a header row — merge its matches into the map
      for (let i = DATA_COL_START; i < Math.max(row.length, COL_SCAN_END); i++) {
        if (colIndexToField.has(i)) continue;
        const header = String(row[i] ?? '').trim().toLowerCase();
        // Strip trailing unit suffixes like " (m²)", " (m)", " (nr)" for matching
        const stripped = header.replace(/\s*\([^)]*\)\s*$/, '').trim();
        const field = SHEET_HEADER_TO_FIELD[stripped] ?? SHEET_HEADER_TO_FIELD[header];
        if (field) colIndexToField.set(i, field);
      }
    }
  }

  // Fill any columns the header scan missed using the known positional
  // layout of the 'Carriageway Take Off' template — guards against header
  // rows that don't carry a per-column label all the way through the
  // Footpaths/Kerbs & Edgings columns, which previously left those fields at
  // their default 0 regardless of the actual imported values.
  POSITIONAL_FIELDS.forEach((field, i) => {
    const colIdx = DATA_COL_START + i;
    if (field && !colIndexToField.has(colIdx)) {
      colIndexToField.set(colIdx, field);
    }
  });

  // Second pass: collect road names and their rows
  // Track: first occurrence = header row, second = data row.
  // Roads whose header row has no text labels in col C are skipped (no headers = no import).
  const roadFirstRow = new Map<string, unknown[]>();
  const results: CarriagewayFootway[] = [];

  for (const rawRow of raw) {
    const row = rawRow as unknown[];
    const roadName = String(row[1] ?? '').trim();
    // A road name that's literally "0" is a placeholder/unfilled cell, same
    // as an empty name — skip it rather than importing a road called "0".
    if (!roadName || roadName === '0' || SECTION_HEADERS.has(roadName.toLowerCase())) continue;

    if (!roadFirstRow.has(roadName)) {
      roadFirstRow.set(roadName, row);
    } else {
      // This is the data row (second occurrence)
      const cf: CarriagewayFootway = {
        id: `imported_cf_${roadName.replace(/\s+/g, '_')}`,
        roadName,
        tarmacSpec1: 0, tarmacSpec2: 0, tarmacSpec3: 0, tarmacSpec4: 0,
        blockPavedSpec1: 0, blockPavedSpec2: 0, blockPavedSpec3: 0, blockPavedSpec4: 0,
        totalRoadArea: 0, kerbRace: 0, dowelBarsInRace: 0, keyChannel: 0,
        filletToCrossover: 0, removeFillet: 0, extrudedKerbRace: 0, tempKerbBlock: 0,
        removeTempKerbBlock: 0, kerb10x5IncRace: 0, kerb10x5OnRace: 0, kerb6x5OnRace: 0,
        dropKerb: 0, delineationKerb: 0, eoRadiusKerb: 0, eoRadiusDropKerb: 0,
        kasselKerb: 0, triefKerb: 0, beanyKerb: 0, beanyGully: 0,
        acoKerbDrain: 0, acoKerbGully: 0, eoRamps: 0,
        tarmacPath: 0, tarmacCrossover: 0, heavyDutyCrossover: 0, blockPavedPathCrossover: 0,
        cycleway: 0, verge: 0, totalPath: 0,
        edgings150x50: 0, edgings200x50: 0, tactilePaving: 0, corduroyPaving: 0,
      };

      for (let i = DATA_COL_START; i < Math.max(row.length, COL_SCAN_END); i++) {
        const field = colIndexToField.get(i);
        if (!field) continue;
        const val = row[i];
        (cf as unknown as Record<string, unknown>)[field] = typeof val === 'number' ? val : (parseFloat(String(val)) || 0);
      }

      // Recalculate derived totals
      cf.totalRoadArea = cf.totalRoadArea || (
        cf.tarmacSpec1 + cf.tarmacSpec2 + cf.tarmacSpec3 + cf.tarmacSpec4 +
        cf.blockPavedSpec1 + cf.blockPavedSpec2 + cf.blockPavedSpec3 + cf.blockPavedSpec4
      );
      cf.totalPath = cf.totalPath || (
        cf.tarmacPath + cf.tarmacCrossover + cf.heavyDutyCrossover +
        cf.blockPavedPathCrossover + cf.cycleway + cf.verge
      );

      results.push(cf);
    }
  }

  console.group('📋 parseCarriagewayAndFootwaysData — result');
  console.table(results.map(r => ({
    roadName: r.roadName,
    tarmacSpec1: r.tarmacSpec1, tarmacSpec2: r.tarmacSpec2,
    tarmacSpec3: r.tarmacSpec3, tarmacSpec4: r.tarmacSpec4,
    blockPavedSpec1: r.blockPavedSpec1, totalRoadArea: r.totalRoadArea,
    kerbRace: r.kerbRace, dropKerb: r.dropKerb, eoRamps: r.eoRamps,
    tarmacPath: r.tarmacPath, tarmacCrossover: r.tarmacCrossover,
    heavyDutyCrossover: r.heavyDutyCrossover, cycleway: r.cycleway,
    verge: r.verge, totalPath: r.totalPath,
    edgings150x50: r.edgings150x50, tactilePaving: r.tactilePaving,
  })));
  console.groupEnd();

  return results;
};

// Read the Strip Value (topsoil strip depth) from cell F76 of the 'Trial Pit Log' sheet.
// Returns null if the sheet or cell is missing so callers can fall back to their default.
export const parseStripValue = async (file: File): Promise<number | null> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const sheetName = workbook.SheetNames.find(
    (n) => n.trim().toLowerCase() === 'trial pit log'
  );
  if (!sheetName) {
    console.warn('parseStripValue: "Trial Pit Log" sheet not found');
    return null;
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // F76 → row index 75 (0-based), col index 5 (A=0…F=5)
  const row = raw[75];
  if (!row) {
    console.warn('parseStripValue: row 76 not found in "Trial Pit Log"');
    return null;
  }

  const cell = row[5];
  const value = typeof cell === 'number' ? cell : parseFloat(String(cell ?? ''));
  if (isNaN(value)) {
    console.warn('parseStripValue: F76 value is not numeric:', cell);
    return null;
  }

  console.log(`parseStripValue: F76 = ${value}`);
  return value;
};

// Read the Kerb Race Width from cell BD18 of the 'Carriageway Take Off' sheet.
// Returns null if the sheet or cell is missing so callers can fall back to their default.
export const parseKerbRaceWidth = async (file: File): Promise<number | null> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('parseKerbRaceWidth: "Carriageway Take Off" sheet not found');
    return null;
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // BD18 → row index 17 (0-based), col index 55 (A=0…BD=55)
  const row = raw[17];
  if (!row) {
    console.warn('parseKerbRaceWidth: row 18 not found in "Carriageway Take Off"');
    return null;
  }

  const cell = row[55];
  const value = typeof cell === 'number' ? cell : parseFloat(String(cell ?? ''));
  if (isNaN(value)) {
    console.warn('parseKerbRaceWidth: BD18 value is not numeric:', cell);
    return null;
  }

  console.log(`parseKerbRaceWidth: BD18 = ${value}`);
  return value;
};

// Read the Tarmac/Block Paved Construction Type selections (Cut & Fill Configure modal) from
// AY22/AY23 of the 'Carriageway Take Off' sheet. Values are resolved the same way as the
// per-row Road Type column (AX) in parseCutFillSections — see CUT_FILL_ROAD_TYPE_MAP below.
export const parseRoadConstructionTypeSelections = async (file: File): Promise<{
  tarmacConstructionTypeId: string | null;
  blockPavedConstructionTypeId: string | null;
}> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('parseRoadConstructionTypeSelections: "Carriageway Take Off" sheet not found');
    return { tarmacConstructionTypeId: null, blockPavedConstructionTypeId: null };
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  const AY = 50; // A=0…AY=50

  const readTypeId = (rowIdx: number): string | null => {
    const nameRaw = String(raw[rowIdx]?.[AY] ?? '').trim();
    if (!nameRaw) return null;
    return resolveCutFillRoadTypeId(nameRaw);
  };

  // AY22 → row index 21, AY23 → row index 22 (0-based)
  const tarmacConstructionTypeId = readTypeId(21);
  const blockPavedConstructionTypeId = readTypeId(22);

  console.log(`parseRoadConstructionTypeSelections: AY22=${tarmacConstructionTypeId}, AY23=${blockPavedConstructionTypeId}`);
  return { tarmacConstructionTypeId, blockPavedConstructionTypeId };
};

const CUT_FILL_ROAD_TYPE_MAP: Record<string, string> = {
  'tarmac 1': 'tarmac-1',
  'tarmac 2': 'tarmac-2',
  'tarmac 3': 'tarmac-3',
  'tarmac 4': 'tarmac-4',
  'block paved 1': 'block-1',
  'block paved 2': 'block-2',
  'block paved 3': 'block-3',
  'block paved 4': 'block-4',
};

// Cells often carry more than the bare label (e.g. "Tarmac 1 - 40mm surface"), so match by
// substring containment against the known labels rather than requiring an exact equal string.
const resolveCutFillRoadTypeId = (nameRaw: string): string => {
  const lower = nameRaw.toLowerCase();
  const matchedKey = Object.keys(CUT_FILL_ROAD_TYPE_MAP).find((key) => lower.includes(key));
  return matchedKey ? CUT_FILL_ROAD_TYPE_MAP[matchedKey] : nameRaw;
};

// Parse carriageway cut & fill + footpaths data from cols AU–BO of 'Carriageway Take Off'.
// Road name in AU is only populated on the first chainage row per road (carry-forward pattern).
// Valid data rows are identified by AV (chainage) being numeric.
export const parseCutFillSections = async (file: File): Promise<CutFillSection[]> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('parseCutFillSections: Carriageway Take Off sheet not found');
    return [];
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  const AU = 46;

  const results: CutFillSection[] = [];
  let currentRoadName = '';

  for (let rowIdx = 0; rowIdx < raw.length; rowIdx++) {
    const row = raw[rowIdx];

    // Skip non-data rows BEFORE touching currentRoadName — inter-road total/config
    // rows can have text in AU that would otherwise corrupt the carry-forward name.
    const chainageRaw = row[AU + 1]; // AV
    const chainageNum = typeof chainageRaw === 'number' ? chainageRaw : parseFloat(String(chainageRaw ?? ''));
    if (isNaN(chainageNum)) continue;

    // Road name only populated on first chainage row per road — carry forward.
    // A road name that's literally "0" is a placeholder/unfilled cell, same
    // as an empty name — don't carry it forward as a real road name.
    const roadNameCell = String(row[AU] ?? '').trim();
    if (roadNameCell && roadNameCell !== '0') currentRoadName = roadNameCell;
    if (!currentRoadName || currentRoadName === '0') continue;

    const n = (offset: number): number => {
      const v = row[AU + offset];
      if (typeof v === 'number') return v;
      const parsed = parseFloat(String(v ?? ''));
      return isNaN(parsed) ? 0 : parsed;
    };

    const roadTypeRaw = String(row[AU + 3] ?? '').trim(); // AX
    const roadTypeId = resolveCutFillRoadTypeId(roadTypeRaw);
    const cfId = `imported_cf_${currentRoadName.replace(/\s+/g, '_')}`;
    const frl = n(2); // AW

    const section: CutFillSection = {
      id: `imported_cfs_${currentRoadName.replace(/\s+/g, '_')}_${chainageNum}`,
      chainage: String(chainageNum),
      carriagewayFootwayId: cfId,
      roadTypeId,
      frl,
      finishedRoadLevel: frl,
      roadConstructionThickness: n(4),  // AY
      formationLevel: n(5),             // AZ
      existingGroundLevel: n(6),        // BA
      stripValue: Math.abs(n(7)),       // BB (less strip — stored as positive)
      lessStrip: n(7),                  // BB (raw calculated value)
      depth: n(8),                      // BC
      cappingArea: n(9),                // BD
      roadArea: n(10),                  // BE
      cutVolume: n(11),                 // BF
      fillVolume: n(12),                // BG
      // BH (offset 13) is a gap column — skip
      fpl: n(14),                       // BI
      pathFormLevel: n(15),             // BJ
      footpathsExistingGroundLevel: 0,  // not in sheet
      footpathsLessStrip: n(16),        // BK
      footpathsDepth: n(17),            // BL
      footpathsCutVolume: n(18),        // BM
      footpathsFillVolume: n(19),       // BN
      pathArea: n(20),                  // BO
    };

    results.push(section);
  }

  console.group('📋 parseCutFillSections — result');
  console.table(
    results.map((r) => ({
      road: r.carriagewayFootwayId,
      chainage: r.chainage,
      roadTypeId: r.roadTypeId,
      frl: r.frl,
      formationLevel: r.formationLevel,
      existingGroundLevel: r.existingGroundLevel,
      depth: r.depth,
      roadArea: r.roadArea,
      cutVolume: r.cutVolume,
      fpl: r.fpl,
      pathFormLevel: r.pathFormLevel,
      footpathsDepth: r.footpathsDepth,
      pathArea: r.pathArea,
    }))
  );
  console.groupEnd();

  return results;
};

// Log cols AU–BO (indices 46–66) to show carriageway cut & fill + footpaths data layout.
// Rows with a non-empty value in col A or col B are included.
export const logCutFillAndFootpathsData = async (file: File): Promise<void> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('logCutFillAndFootpathsData: Carriageway Take Off sheet not found');
    return;
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // AU=46 … BO=66 (0-indexed)
  const COL_START = 46;
  const colLetters = [
    'AU','AV','AW','AX','AY','AZ',
    'BA','BB','BC','BD','BE','BF','BG','BH','BI','BJ','BK','BL','BM','BN','BO',
  ];

  const rows = raw.map((rawRow, rowIdx) => {
    const row = rawRow as unknown[];
    const obj: Record<string, unknown> = { rowIdx, ExcelRow: rowIdx + 1 };
    colLetters.forEach((letter, i) => { obj[letter] = row[COL_START + i]; });
    return obj;
  });

  console.group('📋 logCutFillAndFootpathsData — cols AU–BO');
  console.table(rows);
  console.groupEnd();
};

// Survey the full 'Carriageway Take Off' sheet to find road data and footways data locations
// Logs cols A–E (indices 0–4) for all rows so you can see section headers and row labels
// Then logs cols 0–15 for rows that look like data headers/sections
export const logCarriagewaySheetSurvey = async (file: File): Promise<void> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const candidates = ['Carriageway Take Off', 'Carriageway Take off', 'Carriageway Takeoff', 'CARRIAGEWAY TAKE OFF'];
  const sheetName = candidates.find((n) => workbook.Sheets[n])
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('carriageway'));

  if (!sheetName) {
    console.warn('❌ No "Carriageway Take Off" sheet found');
    return;
  }

  const ws = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  // 1. Row structure overview — cols A–F (0–5) for every row
  console.group(`📋 Sheet "${sheetName}" — row structure overview (cols A–F, all ${raw.length} rows)`);
  const rowOverview = raw.map((r, rowIdx) => {
    const row = r as unknown[];
    return {
      rowIdx,
      ExcelRow: rowIdx + 1,
      A: row[0], B: row[1], C: row[2], D: row[3], E: row[4], F: row[5],
    };
  });
  console.table(rowOverview);
  console.groupEnd();

  // 2. Wider slice — first 30 columns (A–AD) for rows that have non-empty col A or B
  const nonEmptyRows = raw.reduce<number[]>((acc, r, idx) => {
    const row = r as unknown[];
    if (String(row[0] ?? '').trim() || String(row[1] ?? '').trim()) acc.push(idx);
    return acc;
  }, []);

  console.group(`📋 Sheet "${sheetName}" — rows with labels in col A or B (cols 0–29)`);
  const labelRows = nonEmptyRows.map((rowIdx) => {
    const r = (raw[rowIdx] ?? []) as unknown[];
    const obj: Record<string, unknown> = { rowIdx, ExcelRow: rowIdx + 1 };
    for (let c = 0; c < 30; c++) { obj[`c${c}`] = r[c]; }
    return obj;
  });
  console.table(labelRows);
  console.groupEnd();
};

// Validate roads data
export const validateRoadsData = (
  data: RoadsExcelData
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate road types
  if (!data.roadTypes || data.roadTypes.length === 0) {
    errors.push("At least one road type is required");
  }

  // Validate carriageway & footways
  if (!data.carriagewayFootways || data.carriagewayFootways.length === 0) {
    errors.push("At least one carriageway & footways record is required");
  }

  // Validate numeric values
  data.roadTypes?.forEach((roadType, index) => {
    if (!roadType.name) {
      errors.push(`Road Type ${index + 1}: Name is required`);
    }
    if (roadType.surfaceCourse?.thickness < 0) {
      errors.push(
        `Road Type ${index + 1}: Surface thickness cannot be negative`
      );
    }
  });

  data.carriagewayFootways?.forEach((cf, index) => {
    if (!cf.roadName) {
      errors.push(`Carriageway & Footways ${index + 1}: Road name is required`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ── Main Services Trenching parser ───────────────────────────────────────────

export interface MainServiceTrenchingEntry {
  itemId: string;
  roadIndex: number;      // 0-based position of the road column after "Unit"
  roadHeaderName: string; // text from the header row for that column
  quantity: number;
}

/**
 * Reads the "Mains Services" sheet and returns per-item per-road quantities.
 * Duplicate descriptions (e.g. "EO 2 way ducting" appears under both BT and
 * Virgin Media) are resolved in-order: the first occurrence in the sheet maps
 * to the first item in the catalogue with that description, the second to the
 * second, and so on.
 */
export const parseMainServicesTrenching = async (
  file: File,
): Promise<MainServiceTrenchingEntry[]> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', raw: true });

  const sheetName = workbook.SheetNames.find(
    (n: string) => n.trim().toLowerCase() === 'mains services',
  );
  if (!sheetName) return [];

  const ws = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    raw: true,
    defval: null,
  }) as unknown[][];

  // Build ordered description → [itemId, ...] map (preserves duplicate order)
  const descToItemIds = new Map<string, string[]>();
  MAIN_SERVICE_TRENCHING_MAPPING.forEach((group) => {
    group.items.forEach((item) => {
      const key = item.description.trim().toLowerCase();
      if (!descToItemIds.has(key)) descToItemIds.set(key, []);
      descToItemIds.get(key)!.push(item.id);
    });
  });

  // ── Find the header row ───────────────────────────────────────────────────
  let headerRowIdx = -1;
  let descColIdx = 1;
  let unitColIdx = 2;

  for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i];
    for (let j = 0; j < row.length; j++) {
      if (String(row[j] ?? '').trim().toLowerCase() === 'description') {
        headerRowIdx = i;
        descColIdx = j;
        unitColIdx = j + 1;
        for (let k = j + 1; k < row.length; k++) {
          if (String(row[k] ?? '').trim().toLowerCase() === 'unit') {
            unitColIdx = k;
            break;
          }
        }
        break;
      }
    }
    if (headerRowIdx !== -1) break;
  }

  if (headerRowIdx === -1) headerRowIdx = 0;

  // Road columns: every non-empty cell after the Unit column in the header row
  const headerRow = rawRows[headerRowIdx] ?? [];
  const roadColIndices: number[] = [];
  const roadColHeaders: string[] = [];

  for (let j = unitColIdx + 1; j < headerRow.length; j++) {
    const hdr = String(headerRow[j] ?? '').trim();
    if (hdr) {
      roadColIndices.push(j);
      roadColHeaders.push(hdr);
    }
  }

  // ── Parse data rows ───────────────────────────────────────────────────────
  const results: MainServiceTrenchingEntry[] = [];
  const usedCount = new Map<string, number>();

  for (let i = headerRowIdx + 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    const desc = String(row[descColIdx] ?? '').trim();
    if (!desc) continue;

    const key = desc.toLowerCase();
    const itemIds = descToItemIds.get(key);
    if (!itemIds) continue;

    // Consume item IDs in order to handle duplicate descriptions
    const used = usedCount.get(key) ?? 0;
    const itemId = itemIds[used % itemIds.length];
    usedCount.set(key, used + 1);

    roadColIndices.forEach((colIdx, roadIdx) => {
      const raw = row[colIdx];
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
      if (!isNaN(num) && num > 0) {
        results.push({
          itemId,
          roadIndex: roadIdx,
          roadHeaderName: roadColHeaders[roadIdx] ?? '',
          quantity: num,
        });
      }
    });
  }

  return results;
};

// ── Carriageway Summary manual-input quantities parser ───────────────────────

export interface CarriagewaySummaryManualQtyEntry {
  /** Base key without the road-id suffix, e.g. 'footpaths-fill-6f2' or 'hot-bond-1-tarmacSpec2' */
  key: string;
  roadIndex: number;      // 0-based position of the "Road N" column
  roadHeaderName: string; // e.g. "Road 1"
  quantity: number;
}

// Maps a manual row's Description-column label (lowercased, trimmed) to its
// RoadsBillSummary.tsx `manualKey` prefix — every yellow-fill/no-formula row
// on the "Carriageway Summary" sheet EXCEPT "Filling with recycled 6F2 to
// footpaths" and "Hot bond coat between layers" (handled separately below,
// since those repeat under multiple sub-sections and need the current spec
// key threaded through).
const MANUAL_ROW_LABEL_TO_KEY: Record<string, string> = {
  'manholes - single cover and frame': 'ironwork-manholes-single',
  'manholes single cover and frame': 'ironwork-manholes-single',
  'manholes - double cover and frame': 'ironwork-manholes-double',
  'manholes double cover and frame': 'ironwork-manholes-double',
  'gully covers and frames': 'ironwork-gully-covers',
  'stop tap boxes': 'ironwork-stop-tap',
  'bt / virgin footway boxes': 'ironwork-bt-virgin',
  'bt/virgin footway boxes': 'ironwork-bt-virgin',
  'white lining': 'white-lining',
  'fix-only street nameplates': 'street-nameplates',
  'fix only street nameplates': 'street-nameplates',
  'tree pits': 'tree-pits',
  'bollards': 'bollards',
  'kerb remedial': 'remedials-kerb',
  'edging remedial': 'remedials-edging',
  'post & rail fencing': 'post-rail-fencing',
  'post and rail fencing': 'post-rail-fencing',
  'armco barrier': 'armco-barrier',
  'supply & install signs': 'signage-supply-install',
  'supply and install signs': 'signage-supply-install',
  'keep left illuminated bollard': 'signage-keep-left-illuminated',
  'keep left reflective bollard': 'signage-keep-left-reflective',
  'regulating to footpaths': 'regulating-cycleway',
};

/**
 * Reads the "Carriageway Summary" sheet's manual-entry rows — the ones with no
 * live formula link back to "Carriageway Take off" (per docs/R&S-Takeoffsheet.xlsx):
 * "Filling with recycled 6F2 to footpaths" (one row), "Hot bond coat between
 * layers" (twice under each "Tarmac Spec N" sub-section, once under each
 * "BLOCK PAVED N" sub-section), and the Ironwork/Street Furniture/Remedials
 * placeholder rows (Manholes, Gully covers, Stop Tap Boxes, BT/Virgin footway
 * boxes, White lining, Fix-only street nameplates, Tree pits, Bollards, Kerb/
 * Edging remedial, Post & Rail fencing, Armco barrier, Signage) — matching
 * RoadsBillSummary.tsx's manual `footpaths-fill-6f2-${roadId}` /
 * `hot-bond-1-${specKey}-${roadId}` / `hot-bond-2-${specKey}-${roadId}` /
 * `hot-bond-bp-${specKey}-${roadId}` / `${MANUAL_ROW_LABEL_TO_KEY value}-${roadId}` keys.
 */
export const parseCarriagewaySummaryManualQtys = async (
  file: File,
): Promise<CarriagewaySummaryManualQtyEntry[]> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', raw: true });

  const sheetName = workbook.SheetNames.find((n: string) => n.trim().toLowerCase() === 'carriageway summary')
    ?? workbook.SheetNames.find((n: string) => n.toLowerCase().includes('carriageway') && n.toLowerCase().includes('summary'));
  if (!sheetName) return [];

  const ws = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    raw: true,
    defval: null,
  }) as unknown[][];

  // ── Find the header row (has "Description" and "Unit") ────────────────────
  let headerRowIdx = -1;
  let descColIdx = 2;
  let unitColIdx = 3;

  for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i];
    for (let j = 0; j < row.length; j++) {
      if (String(row[j] ?? '').trim().toLowerCase() === 'description') {
        headerRowIdx = i;
        descColIdx = j;
        for (let k = j + 1; k < row.length; k++) {
          if (String(row[k] ?? '').trim().toLowerCase() === 'unit') {
            unitColIdx = k;
            break;
          }
        }
        break;
      }
    }
    if (headerRowIdx !== -1) break;
  }
  if (headerRowIdx === -1) return [];

  // Road columns: only cells after Unit matching "Road N" (skips "Quant"/"Total Quant"/"Check")
  const headerRow = rawRows[headerRowIdx] ?? [];
  const roadColIndices: number[] = [];
  const roadColHeaders: string[] = [];
  for (let j = unitColIdx + 1; j < headerRow.length; j++) {
    const hdr = String(headerRow[j] ?? '').trim();
    if (/^road\s*\d+$/i.test(hdr)) {
      roadColIndices.push(j);
      roadColHeaders.push(hdr);
    }
  }

  const results: CarriagewaySummaryManualQtyEntry[] = [];
  let currentSpecKey: string | null = null;
  let hotBondCount = 0;

  for (let i = headerRowIdx + 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    const desc = String(row[descColIdx] ?? '').trim();
    if (!desc) continue;
    const label = desc.toLowerCase();

    const tarmacMatch = label.match(/^tarmac spec (\d+)$/);
    const blockMatch = label.match(/^block paved (\d+)$/);
    if (tarmacMatch) {
      currentSpecKey = `tarmacSpec${tarmacMatch[1]}`;
      hotBondCount = 0;
      continue;
    }
    if (blockMatch) {
      currentSpecKey = `blockPavedSpec${blockMatch[1]}`;
      hotBondCount = 0;
      continue;
    }

    let key: string | null = null;
    if (label === 'filling with recycled 6f2 to footpaths') {
      key = 'footpaths-fill-6f2';
    } else if (label === 'hot bond coat between layers' && currentSpecKey) {
      hotBondCount++;
      if (currentSpecKey.startsWith('tarmacSpec')) {
        key = hotBondCount === 1 ? `hot-bond-1-${currentSpecKey}` : `hot-bond-2-${currentSpecKey}`;
      } else if (currentSpecKey.startsWith('blockPavedSpec')) {
        key = `hot-bond-bp-${currentSpecKey}`;
      }
    } else if (MANUAL_ROW_LABEL_TO_KEY[label]) {
      key = MANUAL_ROW_LABEL_TO_KEY[label];
    }
    if (!key) continue;

    roadColIndices.forEach((colIdx, roadIdx) => {
      const raw = row[colIdx];
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
      if (!isNaN(num) && num > 0) {
        results.push({
          key: key as string,
          roadIndex: roadIdx,
          roadHeaderName: roadColHeaders[roadIdx] ?? '',
          quantity: num,
        });
      }
    });
  }

  return results;
};

export interface CappingLayersImportResult {
  thicknesses: number[];
  t1ThicknessMm: Record<string, number>;
  /** "Add exc"/"Add 6F2 fill" manual Quant values, per capping thickness section —
   * keyed `capping-{mm}-add-exc`/`capping-{mm}-add-6f2` to match roadsData.manualQtys
   * (see RoadsTakeoff.tsx's Capping Layers tab). */
  manualQtys: Record<string, number>;
}

// Parses the "Capping Layers" take-off sheet, which mirrors RoadsTakeoff.tsx's own
// Capping Layer tab layout: each section starts with a "<mm>mm CAPPING" header row
// (optionally prefixed "A. "), followed by item rows including "Omit <mm>mm thick
// Type 1 to tarmac/block paved road" — collects each section's own mm plus any
// Type 1 thickness configured for those two rows (see CappingLayer.t1ThicknessMm,
// set via the tab's Configure gear icon), plus the "Add exc"/"Add 6F2 fill" manual
// Quant values, read from column E (index 4) — those two rows have no formula
// (their description carries no thickness/number of its own, unlike "Omit 6F2
// fill"/"Omit xxmm thick Type 1..." etc.), so unlike every other row here they
// can't be derived from the row's own text and must be read as a plain number.
export const parseCappingLayersFromExcel = async (file: File): Promise<CappingLayersImportResult> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', raw: true });

  const sheetName = workbook.SheetNames.find((n) => n.trim().toLowerCase() === 'capping layers')
    ?? workbook.SheetNames.find((n) => n.toLowerCase().includes('capping'));
  if (!sheetName) return { thicknesses: [], t1ThicknessMm: {}, manualQtys: {} };

  const ws = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' }) as unknown[][];

  const SECTION_MM_RE = /(\d+)\s*mm\s*capping/i;
  const ITEM_KEYWORD_RE = /\b(omit|add|stockpile|eo)\b/i;
  const TARMAC_RE = /omit\s+(\d+)\s*mm\s+thick\s+type\s*1\s+to\s+tarmac\s+road/i;
  const BLOCK_RE = /omit\s+(\d+)\s*mm\s+thick\s+type\s*1\s+to\s+block\s+paved\s+road/i;
  const ADD_EXC_RE = /^add\s+exc$/i;
  const ADD_6F2_RE = /^add\s+6f2\s+fill$/i;
  const QUANT_COL_INDEX = 4; // column E

  const thicknesses = new Set<number>();
  const t1ThicknessMm: Record<string, number> = {};
  const manualQtys: Record<string, number> = {};
  let currentSectionMm: number | null = null;

  for (const row of rawRows) {
    const rowText = row.map((c) => String(c ?? '').trim()).filter(Boolean).join(' ').trim();
    if (!rowText) continue;

    const sectionMatch = rowText.match(SECTION_MM_RE);
    if (sectionMatch && !ITEM_KEYWORD_RE.test(rowText)) {
      currentSectionMm = Number(sectionMatch[1]);
      thicknesses.add(currentSectionMm);
      continue;
    }

    if (currentSectionMm == null) continue;

    const tarmacMatch = rowText.match(TARMAC_RE);
    if (tarmacMatch) {
      t1ThicknessMm[`${currentSectionMm}-tarmac`] = Number(tarmacMatch[1]);
      continue;
    }

    const blockMatch = rowText.match(BLOCK_RE);
    if (blockMatch) {
      t1ThicknessMm[`${currentSectionMm}-block`] = Number(blockMatch[1]);
      continue;
    }

    // Description sits in column C (index 2), the Quant value in column E —
    // unlike the section/tarmac/block matches above, this isn't embedded in the
    // row's own text, so read it directly off the cell rather than the regex.
    const description = String(row[2] ?? '').trim();
    if (!description) continue;
    const isAddExc = ADD_EXC_RE.test(description);
    const isAdd6F2 = ADD_6F2_RE.test(description);
    if (isAddExc || isAdd6F2) {
      const rawQty = row[QUANT_COL_INDEX];
      const qty = typeof rawQty === 'number' ? rawQty : parseFloat(String(rawQty));
      if (!isNaN(qty)) {
        manualQtys[`capping-${currentSectionMm}-${isAddExc ? 'add-exc' : 'add-6f2'}`] = qty;
      }
    }
  }

  console.log('parseCappingLayersFromExcel:', { sheetName, thicknesses: Array.from(thicknesses), t1ThicknessMm, manualQtys });
  return { thicknesses: Array.from(thicknesses).sort((a, b) => a - b), t1ThicknessMm, manualQtys };
};
